import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Receipt, Upload, Loader2, CheckCircle2, AlertTriangle, FileText, Trash2, X,
  Building2, ListChecks, RefreshCw, ShieldCheck, Link2, ChevronsUpDown, Check, Search,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SolicitudFacturaTab from "@/pages/facturas/SolicitudFacturaTab";
import PorEstadoTab from "@/pages/facturas/PorEstadoTab";
import SubirFacturasTab from "@/pages/facturas/SubirFacturasTab";
import TodasTab from "@/pages/facturas/TodasTab";

const EDITOR_ROLES = new Set(["agente_facturacion", "contable", "administracion", "socio_director", "dir_admin_general"]);
const eur = (n) => (n == null ? "—" : `${Number(n).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

// ============================= Subir factura =============================
function ProviderCombobox({ providers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const sel = providers.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between font-normal" data-testid="fm-upload-provider">
          <span className={`flex items-center gap-2 truncate ${sel ? "" : "text-muted-foreground"}`}>
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            {sel ? `${sel.name}${sel.pendientes ? ` · ${sel.pendientes} pend.` : ""}` : "Busca proveedor (escribe 3 letras)…"}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Escribe el nombre del proveedor…" data-testid="fm-upload-provider-search" />
          <CommandList>
            <CommandEmpty>Sin proveedores mensuales que coincidan.</CommandEmpty>
            <CommandGroup>
              {providers.map((p) => (
                <CommandItem key={p.id} value={`${p.name} ${p.nombre_comercial || ""} ${p.cif || ""}`}
                  onSelect={() => { onChange(p.id); setOpen(false); }} data-testid={`fm-upload-provider-opt-${p.id}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === p.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1 truncate">{p.name}</span>
                  {p.pendientes > 0 && <Badge variant="secondary" className="ml-2 text-[10px]">{p.pendientes} pend.</Badge>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function UploadDialog({ open, onClose, providers, onCreated }) {
  const [pid, setPid] = useState("");
  const [file, setFile] = useState(null);
  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("provider_id", pid);
      fd.append("file", file);
      return (await api.post("/facturas-mensuales", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data;
    },
    onSuccess: (d) => { toast.success("Factura leída y conciliada"); setPid(""); setFile(null); onCreated(d); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo procesar la factura")),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fm-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4 text-primary" />Subir factura mensual</DialogTitle>
          <DialogDescription className="text-xs">La IA leerá la factura y la cotejará con los servicios acumulados del proveedor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Proveedor (factura mensual)</label>
            <ProviderCombobox providers={providers} value={pid} onChange={setPid} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">PDF de la factura</label>
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-primary" data-testid="fm-upload-file" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!pid || !file || mut.isPending} data-testid="fm-upload-submit">
            {mut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Leyendo con IA…</> : <><Upload className="mr-1.5 h-4 w-4" />Subir y conciliar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================= Conciliación =============================
function ReconcileDialog({ fmId, canEdit, onClose }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["fm", fmId], queryFn: async () => (await api.get(`/facturas-mensuales/${fmId}`)).data, enabled: !!fmId });
  const d = q.data;
  const pool = [...(d?.reservas_incluidas || []), ...(d?.reservas_no_incluidas || [])];

  const matchMut = useMutation({
    mutationFn: async ({ lid, reserva_id }) => (await api.patch(`/facturas-mensuales/${fmId}/lineas/${lid}`, { reserva_id })).data,
    onSuccess: (res) => qc.setQueryData(["fm", fmId], res),
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const inclMut = useMutation({
    mutationFn: async (ids) => (await api.patch(`/facturas-mensuales/${fmId}/incluidas`, { reserva_ids: ids })).data,
    onSuccess: (res) => qc.setQueryData(["fm", fmId], res),
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const validarMut = useMutation({
    mutationFn: async (forzar) => (await api.post(`/facturas-mensuales/${fmId}/validar`, { forzar })).data,
    onSuccess: (res) => { toast.success("Factura validada · entra en el próximo cierre"); qc.setQueryData(["fm", fmId], res); qc.invalidateQueries({ queryKey: ["fm-list"] }); qc.invalidateQueries({ queryKey: ["fm-proveedores"] }); },
    onError: (e) => {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.discrepancias) {
        if (window.confirm("Hay discrepancias:\n\n" + det.discrepancias.join("\n") + "\n\n¿Validar de todas formas?")) validarMut.mutate(true);
      } else toast.error(apiErrorMessage(e));
    },
  });

  const toggleIncl = (rid) => {
    const ids = new Set((d.reservas_incluidas || []).map((r) => r.id));
    ids.has(rid) ? ids.delete(rid) : ids.add(rid);
    inclMut.mutate([...ids]);
  };

  const validada = d?.estado === "validada";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[94vw] w-[94vw] p-0 gap-0" data-testid="fm-reconcile-dialog">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
            <Receipt className="h-4 w-4 text-primary" /> {d?.provider_name} · Factura {d?.numero || "—"}
            {validada
              ? <Badge className="bg-emerald-600">Validada</Badge>
              : <Badge variant="outline" className="border-amber-300 text-amber-700">Conciliando</Badge>}
            <Badge variant="outline" className="ml-1 text-[10px] capitalize">{d?.modalidad}</Badge>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Fecha {d?.fecha || "—"} · Total {eur(d?.total)} · IVA {eur(d?.iva_amount)} · Cotejo de líneas/total contra los servicios acumulados.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[80vh] overflow-y-auto p-4 space-y-4">
          {q.isLoading || !d ? <Skeleton className="h-64 w-full" /> : (
            <>
              {/* Banner discrepancias */}
              {d.discrepancias?.length > 0 ? (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="fm-discrepancias">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div><p className="font-semibold">{d.discrepancias.length} discrepancia(s):</p><ul className="list-disc pl-4">{d.discrepancias.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="fm-conciliada-ok">
                  <CheckCircle2 className="h-4 w-4" /> Todo cuadra. Lista para validar.
                </div>
              )}

              {/* Totales */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[["Total factura", d.totales?.factura_total], ["Suma servicios (c/IVA)", d.totales?.suma_incl],
                  ["Base factura", d.totales?.factura_base], ["Suma servicios (s/IVA)", d.totales?.suma_excl]].map(([k, v], i) => (
                  <div key={i} className="rounded-lg border bg-card px-3 py-2"><p className="text-[10px] uppercase text-muted-foreground">{k}</p><p className="text-sm font-bold tabular-nums">{eur(v)}</p></div>
                ))}
              </div>

              {/* Líneas (desglosada) */}
              {d.modalidad === "desglosada" && (
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold"><ListChecks className="h-4 w-4" />Líneas de la factura</p>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-zinc-50 text-[10px] uppercase text-muted-foreground">
                        <tr><th className="px-3 py-2">Concepto</th><th className="px-3 py-2">Cód./Huésped</th><th className="px-3 py-2">Fecha</th><th className="px-3 py-2 text-right">Importe</th><th className="px-3 py-2">Reserva (servicio)</th><th className="px-3 py-2 text-right">Esperado</th><th className="px-3 py-2 text-center">Estado</th></tr>
                      </thead>
                      <tbody>
                        {d.lineas.map((l) => (
                          <tr key={l.id} className={`border-t ${l.ok ? "" : "bg-amber-50/40"}`} data-testid={`fm-line-${l.id}`}>
                            <td className="px-3 py-2 max-w-[200px] truncate" title={l.descripcion}>{l.descripcion}</td>
                            <td className="px-3 py-2">{l.codigo || "—"}</td>
                            <td className="px-3 py-2 text-xs">{l.fecha || "—"}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{eur(l.importe)}</td>
                            <td className="px-3 py-2">
                              {canEdit && !validada ? (
                                <Select value={l.reserva?.id || "none"} onValueChange={(v) => matchMut.mutate({ lid: l.id, reserva_id: v === "none" ? null : v })}>
                                  <SelectTrigger className="h-8 w-[220px] text-xs" data-testid={`fm-line-match-${l.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">— Sin reserva —</SelectItem>
                                    {pool.map((r) => <SelectItem key={r.id} value={r.id}>{r.guest} · {r.service?.slice(0, 24)} · {eur(r.invoice_tax_incl)}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              ) : (l.reserva ? <span className="text-xs">{l.reserva.guest} · {l.reserva.service?.slice(0, 24)}</span> : <span className="text-xs text-muted-foreground">—</span>)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-xs">{l.esperado != null ? eur(l.esperado) : "—"}</td>
                            <td className="px-3 py-2 text-center">
                              {l.ok ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">OK</Badge>
                                : <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-700" title={l.issues?.join(", ")}>Revisar</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Servicios incluidos / no incluidos */}
              <div className="grid gap-4 lg:grid-cols-2">
                <ReservaList title={`Servicios incluidos (${d.reservas_incluidas?.length || 0})`} icon={Link2} rows={d.reservas_incluidas} included canEdit={canEdit && !validada} onToggle={toggleIncl} testid="fm-incluidas" />
                <ReservaList title={`No incluidos del proveedor (${d.reservas_no_incluidas?.length || 0})`} icon={Building2} rows={d.reservas_no_incluidas} included={false} canEdit={canEdit && !validada} onToggle={toggleIncl} testid="fm-no-incluidas" />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="border-t px-5 py-3">
          <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/facturas-mensuales/${fmId}/pdf`, "_blank")} data-testid="fm-view-pdf"><FileText className="mr-1.5 h-4 w-4" />Ver PDF</Button>
          {!validada && canEdit && (
            <Button onClick={() => validarMut.mutate(false)} disabled={validarMut.isPending} data-testid="fm-validar">
              {validarMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Validando…</> : <><ShieldCheck className="mr-1.5 h-4 w-4" />Validar factura</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReservaList({ title, icon: Icon, rows, included, canEdit, onToggle, testid }) {
  return (
    <div className="rounded-lg border" data-testid={testid}>
      <p className="flex items-center gap-1.5 border-b bg-zinc-50 px-3 py-2 text-sm font-semibold"><Icon className="h-4 w-4" />{title}</p>
      <div className="max-h-56 divide-y overflow-y-auto">
        {(rows || []).length === 0 ? <p className="px-3 py-4 text-center text-xs text-muted-foreground">—</p> :
          (rows || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm" data-testid={`${testid}-row-${r.id}`}>
              <div className="min-w-0">
                <p className="truncate font-medium">{r.guest || "—"}</p>
                <p className="truncate text-[11px] text-muted-foreground">{r.service} · {r.fecha?.slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="tabular-nums text-xs">{eur(r.invoice_tax_incl)}</span>
                {canEdit && <Checkbox checked={included} onCheckedChange={() => onToggle(r.id)} data-testid={`${testid}-chk-${r.id}`} />}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// ============================= Pestaña Mensuales =============================
function MensualesTab({ canEdit }) {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [openFm, setOpenFm] = useState(null);

  const provQ = useQuery({ queryKey: ["fm-proveedores"], queryFn: async () => (await api.get("/facturas-mensuales/proveedores")).data });
  const listQ = useQuery({ queryKey: ["fm-list"], queryFn: async () => (await api.get("/facturas-mensuales")).data });
  const providers = provQ.data?.items || [];
  const activeProviders = providers.filter((p) => p.pendientes > 0);
  const facturas = listQ.data?.items || [];

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/facturas-mensuales/${id}`)).data,
    onSuccess: () => { toast.success("Factura eliminada"); qc.invalidateQueries({ queryKey: ["fm-list"] }); qc.invalidateQueries({ queryKey: ["fm-proveedores"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Proveedores con factura mensual y conciliación de sus servicios.</p>
        {canEdit && <Button onClick={() => setUploadOpen(true)} data-testid="fm-new-btn"><Upload className="mr-1.5 h-4 w-4" />Subir factura mensual</Button>}
      </div>

      {/* Proveedores mensuales */}
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
          <Building2 className="h-4 w-4" />Proveedores mensuales
          {providers.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] text-muted-foreground">{providers.length} marcados</Badge>}
        </div>
        {provQ.isLoading ? <Skeleton className="h-24 w-full" /> : providers.length === 0 ? (
          <Card><CardContent className="py-6 text-center text-sm text-muted-foreground" data-testid="fm-no-providers">
            Marca proveedores como <strong>"Factura mensual"</strong> en el maestro de Proveedores para que acumulen sus servicios aquí.
          </CardContent></Card>
        ) : activeProviders.length === 0 ? (
          <Card><CardContent className="py-6 text-center text-sm text-muted-foreground" data-testid="fm-no-pending-providers">
            Ningún proveedor mensual tiene servicios acumulados pendientes ahora mismo. Los <strong>{providers.length}</strong> proveedores
            marcados están disponibles en <strong>"Subir factura mensual"</strong> (buscador predictivo).
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="fm-providers">
            {activeProviders.map((p) => (
              <Card key={p.id} data-testid={`fm-prov-${p.id}`}><CardContent className="p-4">
                <p className="font-semibold">{p.name}</p>
                <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                  <span><strong className="text-foreground">{p.pendientes}</strong> servicios pend.</span>
                  <span className="tabular-nums">{eur(p.importe_pendiente_incl)}</span>
                </div>
                {!p.cuenta_contable && <Badge variant="outline" className="mt-2 border-amber-200 text-[10px] text-amber-700">Sin cuenta contable</Badge>}
              </CardContent></Card>
            ))}
          </div>
        )}
      </div>

      {/* Facturas mensuales */}
      <div>
        <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Receipt className="h-4 w-4" />Facturas mensuales
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => listQ.refetch()}><RefreshCw className="h-3.5 w-3.5" /></Button></p>
        {listQ.isLoading ? <Skeleton className="h-24 w-full" /> : facturas.length === 0 ? (
          <p className="rounded-lg border py-6 text-center text-sm text-muted-foreground" data-testid="fm-empty">Aún no hay facturas mensuales.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border" data-testid="fm-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase text-muted-foreground">
                <tr><th className="px-3 py-2">Proveedor</th><th className="px-3 py-2">Nº</th><th className="px-3 py-2">Fecha</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-center">Estado</th><th className="px-3 py-2" /></tr>
              </thead>
              <tbody>
                {facturas.map((f) => (
                  <tr key={f.id} className="border-t hover:bg-zinc-50/50" data-testid={`fm-row-${f.id}`}>
                    <td className="px-3 py-2 font-medium">{f.provider_name}</td>
                    <td className="px-3 py-2">{f.numero || "—"}</td>
                    <td className="px-3 py-2 text-xs">{f.fecha || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{eur(f.total)}</td>
                    <td className="px-3 py-2 text-center">
                      {f.estado === "validada" ? <Badge className="bg-emerald-600 text-[10px]">Validada</Badge> : <Badge variant="outline" className="border-amber-300 text-[10px] text-amber-700">Conciliando</Badge>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" className="h-8" onClick={() => setOpenFm(f.id)} data-testid={`fm-open-${f.id}`}>Conciliar</Button>
                      {canEdit && f.estado !== "validada" && (
                        <Button size="icon" variant="ghost" className="ml-1 h-8 w-8 text-destructive" onClick={() => { if (window.confirm("¿Eliminar esta factura mensual?")) delMut.mutate(f.id); }} data-testid={`fm-del-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} providers={providers} onCreated={(d) => { setUploadOpen(false); qc.invalidateQueries({ queryKey: ["fm-list"] }); qc.invalidateQueries({ queryKey: ["fm-proveedores"] }); setOpenFm(d.id); }} />
      {openFm && <ReconcileDialog fmId={openFm} canEdit={canEdit} onClose={() => setOpenFm(null)} />}
    </div>
  );
}

export default function FacturasPage() {
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.has(user?.role);
  const [params] = useSearchParams();
  const view = params.get("view") || "";

  if (view === "mensuales") {
    return (
      <div className="space-y-5" data-testid="facturas-page">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><Receipt className="h-6 w-6 text-primary" />Facturas mensuales</h1>
          <p className="text-sm text-muted-foreground">Conciliación de proveedores que facturan de forma mensual (incluidas/no incluidas, discrepancias y líneas Holded).</p>
        </div>
        <MensualesTab canEdit={canEdit} />
      </div>
    );
  }

  if (view === "subir") {
    return (
      <div className="space-y-5" data-testid="facturas-page">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><Receipt className="h-6 w-6 text-primary" />Subir Facturas</h1>
          <p className="text-sm text-muted-foreground">Atajo para subir el PDF de la factura del proveedor y completar sus datos, filtrando por viaje/cliente sin entrar en cada reserva.</p>
        </div>
        <SubirFacturasTab />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="facturas-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><Receipt className="h-6 w-6 text-primary" />Solicitud de factura</h1>
        <p className="text-sm text-muted-foreground">Solicita a los proveedores la factura de cada reserva: por viaje y vista global por estado.</p>
      </div>
      <Tabs defaultValue="req" className="w-full">
        <TabsList data-testid="solicitud-subtabs">
          <TabsTrigger value="req" data-testid="solicitud-subtab-req">Solicitud de factura</TabsTrigger>
          <TabsTrigger value="estado" data-testid="solicitud-subtab-estado">Facturas por Estado</TabsTrigger>
          <TabsTrigger value="todas" data-testid="solicitud-subtab-todas">Todas</TabsTrigger>
        </TabsList>
        <TabsContent value="req" className="mt-4"><SolicitudFacturaTab /></TabsContent>
        <TabsContent value="estado" className="mt-4"><PorEstadoTab /></TabsContent>
        <TabsContent value="todas" className="mt-4"><TodasTab /></TabsContent>
      </Tabs>
    </div>
  );
}
